// src/components/Notifications/GlobalToast.jsx
import React, { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  CheckCircle,
  XCircle,
  Info,
  X,
  AlertTriangle,
  Award,
  Clock
} from "lucide-react";
import { useIsMobile } from "../../hooks/useMediaQuery"; // 👈 NUEVO IMPORT

// ============================================================================
// CONFIG
// ============================================================================

const TOAST_TYPES = {
  success: {
    icon: CheckCircle,
    bg: "bg-white/90 backdrop-blur-xl",
    border: "border-emerald-300/30",
    text: "text-emerald-700",
    iconColor: "text-emerald-500",
    shadow: "shadow-2xl shadow-black/5",
    gradient: "from-emerald-500 to-emerald-600"
  },
  error: {
    icon: XCircle,
    bg: "bg-white/90 backdrop-blur-xl",
    border: "border-rose-300/30",
    text: "text-rose-700",
    iconColor: "text-rose-500",
    shadow: "shadow-2xl shadow-black/5",
    gradient: "from-rose-500 to-rose-600"
  },
  warning: {
    icon: AlertTriangle,
    bg: "bg-white/90 backdrop-blur-xl",
    border: "border-amber-300/30",
    text: "text-amber-700",
    iconColor: "text-amber-500",
    shadow: "shadow-2xl shadow-black/5",
    gradient: "from-amber-500 to-amber-600"
  },
  info: {
    icon: Info,
    bg: "bg-white/90 backdrop-blur-xl",
    border: "border-blue-300/30",
    text: "text-blue-700",
    iconColor: "text-blue-500",
    shadow: "shadow-2xl shadow-black/5",
    gradient: "from-blue-500 to-blue-600"
  },
  achievement: {
    icon: Award,
    bg: "bg-white/90 backdrop-blur-xl",
    border: "border-purple-300/30",
    text: "text-purple-700",
    iconColor: "text-purple-500",
    shadow: "shadow-2xl shadow-black/5",
    gradient: "from-purple-500 to-purple-600"
  }
};

const positions = {
  "top-right": "top-4 right-4",
  "top-left": "top-4 left-4",
  "bottom-right": "bottom-4 right-4",
  "bottom-left": "bottom-4 left-4",
  "top-center": "top-4 left-1/2 -translate-x-1/2",
  "bottom-center": "bottom-4 left-1/2 -translate-x-1/2"
};

const positionsMobile = {
  "top-right": "top-3 right-3",
  "top-left": "top-3 left-3",
  "bottom-right": "bottom-3 right-3",
  "bottom-left": "bottom-3 left-3",
  "top-center": "top-3 left-1/2 -translate-x-1/2 w-[calc(100%-24px)]",
  "bottom-center": "bottom-3 left-1/2 -translate-x-1/2 w-[calc(100%-24px)]"
};

const animations = {
  slideIn: {
    initial: { opacity: 0, x: 100, scale: 0.9 },
    animate: { opacity: 1, x: 0, scale: 1 },
    exit: { opacity: 0, x: 100, scale: 0.9 },
    transition: { type: "spring", stiffness: 500, damping: 30 }
  }
};

// ============================================================================
// GLOBAL TOAST
// ============================================================================

export const GlobalToast = ({
  toast,
  onClose,
  defaultPosition = "bottom-right",
  defaultAnimation = "slideIn",
  showProgress = true,
  showIcon = true,
  showCloseButton = true,
  pauseOnHover = true
}) => {
  const isMobile = useIsMobile(); // 👈 DETECTAR MÓVIL

  const [progress, setProgress] = useState(100);
  const [isHovered, setIsHovered] = useState(false);
  const [isPaused] = useState(false);

  const id = toast?.id ?? null;
  const message = toast?.message ?? "";
  const type = toast?.type ?? "info";
  const duration = toast?.duration ?? 4000;
  const title = toast?.title ?? "";
  const position = toast?.position ?? defaultPosition;
  const animation = toast?.animation ?? defaultAnimation;
  const metadata = toast?.metadata ?? null;
  const actions = toast?.actions ?? [];
  const CustomIcon = toast?.icon ?? null;

  const config = TOAST_TYPES[type] || TOAST_TYPES.info;
  const Icon = CustomIcon || config.icon;
  const positionClass = isMobile 
    ? (positionsMobile[position] || positionsMobile["top-right"])
    : (positions[position] || positions["top-right"]);
  const animationConfig = animations[animation] || animations.slideIn;

  // ==========================================================
  // progress bar
  // ==========================================================

  useEffect(() => {
    if (!toast) return;

    let frame;
    let start = Date.now();

    const update = () => {
      const elapsed = Date.now() - start;
      const remaining = Math.max(duration - elapsed, 0);
      const value = (remaining / duration) * 100;

      setProgress(value);

      if (remaining > 0) {
        frame = requestAnimationFrame(update);
      }
    };

    frame = requestAnimationFrame(update);

    return () => {
      if (frame) cancelAnimationFrame(frame);
    };
  }, [toast, duration]);

  // ==========================================================
  // autoclose
  // ==========================================================

  useEffect(() => {
    if (!toast) return;
    if (duration === Infinity) return;

    const timer = setTimeout(() => {
      onClose?.();
    }, duration);

    return () => clearTimeout(timer);
  }, [toast, duration, onClose]);

  // ==========================================================
  // keyboard
  // ==========================================================

  useEffect(() => {
    const esc = (e) => {
      if (e.key === "Escape") {
        onClose?.();
      }
    };

    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [onClose]);

  // ==========================================================
  // RENDER - ADAPTADO
  // ==========================================================

  return (
    <AnimatePresence>
      {toast && (
        <motion.div
          key={id}
          {...animationConfig}
          className={`fixed ${positionClass} z-[99999] max-w-sm w-full`}
          onMouseEnter={() => pauseOnHover && setIsHovered(true)}
          onMouseLeave={() => pauseOnHover && setIsHovered(false)}
        >
          <div
            className={`relative overflow-hidden rounded-3xl border shadow-xl ${config.bg} ${config.border}`}
          >
            <div
              className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${config.gradient}`}
            />

            <div className={`${isMobile ? 'p-3' : 'p-4'}`}>
              <div className={`flex items-start gap-2`}>
                
                {/* Icono - ADAPTADO */}
                {showIcon && (
                  <div
                    className={`flex items-center justify-center ${config.bg} border ${config.border} ${
                      isMobile ? 'w-8 h-8 rounded-lg' : 'w-10 h-10 rounded-xl'
                    }`}
                  >
                    <Icon className={isMobile ? 'w-4 h-4' : 'w-5 h-5'} style={{ color: config.iconColor.split('-')[1] }} />
                  </div>
                )}

                <div className="flex-1 min-w-0">
                  {title && (
                    <h4 className={`font-semibold ${config.text} ${
                      isMobile ? 'text-sm' : ''
                    }`}>
                      {title}
                    </h4>
                  )}

                  <p className={`${config.text} ${
                    isMobile ? 'text-xs' : 'text-sm'
                  }`}>{message}</p>

                  {metadata?.time && (
                    <div className={`opacity-70 mt-0.5 flex items-center gap-1 ${
                      isMobile ? 'text-[9px]' : 'text-xs'
                    }`}>
                      <Clock size={isMobile ? 8 : 10} />
                      {metadata.time}
                    </div>
                  )}

                  {/* Acciones - ADAPTADAS */}
                  {actions.length > 0 && (
                    <div className={`flex gap-1 ${
                      isMobile ? 'mt-2' : 'mt-3'
                    }`}>
                      {actions.map((action, i) => (
                        <button
                          key={i}
                          onClick={() => {
                            action.onClick?.();
                            if (action.closeOnClick !== false) onClose?.();
                          }}
                          className={`rounded-lg border ${config.border} touch:active:scale-95 ${
                            isMobile 
                              ? 'px-2 py-1 text-[9px]' 
                              : 'px-3 py-1 text-xs'
                          }`}
                        >
                          {action.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Botón cerrar - ADAPTADO */}
                {showCloseButton && (
                  <button
                    onClick={onClose}
                    className={`rounded-lg hover:bg-black/5 transition-colors touch:active:scale-95 ${
                      isMobile ? 'p-1' : 'p-1'
                    }`}
                    aria-label="Fechar notificação"
                  >
                    <X className={`${isMobile ? 'w-3 h-3' : 'w-4 h-4'} ${config.text}`} />
                  </button>
                )}
              </div>
            </div>

            {/* Progress bar */}
            {showProgress && duration !== Infinity && (
              <div className="h-1 bg-black/5">
                <motion.div
                  animate={{ width: `${progress}%` }}
                  transition={{ duration: 0.1 }}
                  className={`h-full bg-gradient-to-r ${config.gradient}`}
                />
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};